// src/pages/AuditLogPage.tsx — Nexus-themed audit log
// Replaces admin/src/pages/AuditLogPage.tsx
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';

interface AuditEventRow {
  timestamp: string;
  event: string;
  requestId: string;
  actor: number;
  actorEmail: string;
  clientId: string;
  level: string;
}

interface AuditLogResponse { events: AuditEventRow[]; since: string; until: string; }

const levelColor: Record<string, string> = {
  info:     'text-ink-3',
  warn:     'text-warn-soft',
  error:    'text-err-soft',
  critical: 'text-err-soft',
};

export function AuditLogPage() {
  const [eventFilter, setEventFilter] = useState('');
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');

  const params = new URLSearchParams();
  if (eventFilter) params.set('event', eventFilter);
  if (clientIdFilter) params.set('clientId', clientIdFilter);
  if (actorFilter) params.set('actor', actorFilter);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-log', eventFilter, clientIdFilter, actorFilter],
    queryFn: ({ signal }) => api<AuditLogResponse>(`/api/audit-log${qs}`, { signal }),
  });

  return (
    <div>
      <div className="eo-eyebrow">Audit Trail</div>
      <h1 className="eo-h1 mb-5">Audit Log</h1>

      <div className="eo-card mb-5 flex flex-wrap items-end gap-3.5">
        <label className="block flex-1 min-w-[200px]">
          <span className="eo-label">Event</span>
          <input type="text" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
                 placeholder="e.g. admin.client.create" className="eo-input" />
        </label>
        <label className="block flex-1 min-w-[180px]">
          <span className="eo-label">Client ID</span>
          <input type="text" value={clientIdFilter} onChange={(e) => setClientIdFilter(e.target.value)}
                 placeholder="e.g. name-generator" className="eo-input" />
        </label>
        <label className="block w-40">
          <span className="eo-label">Actor (forum_member_id)</span>
          <input type="text" value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}
                 placeholder="e.g. 42" className="eo-input" />
        </label>
        <button type="button" onClick={() => refetch()} className="eo-btn">Refresh</button>
      </div>

      {isLoading && <p className="text-sm text-ink-3">Loading…</p>}
      {error && <p className="text-sm text-err">Failed to load: {(error as Error).message}</p>}

      {data && (
        <>
          <p className="eo-hint mb-2" style={{marginTop:0}}>
            Showing events from {new Date(data.since).toLocaleString()} to {new Date(data.until).toLocaleString()} · {data.events.length} row{data.events.length === 1 ? '' : 's'}
          </p>
          {data.events.length === 0 ? (
            <p className="text-sm text-ink-3">No events match these filters.</p>
          ) : (
            <table className="eo-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Actor</th>
                  <th>Client</th>
                  <th>Level</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr key={`${e.timestamp}-${e.requestId}`}>
                    <td className="text-ink-3 whitespace-nowrap text-[12px]">{new Date(e.timestamp).toLocaleString()}</td>
                    <td><span className="eo-mono text-[12px]">{e.event}</span></td>
                    <td className="text-[12px]">{e.actorEmail || e.actor}</td>
                    <td><span className="eo-mono text-[12px] text-ink-3">{e.clientId}</span></td>
                    <td>
                      <span className={`font-mono text-[11px] tracking-label uppercase ${levelColor[e.level] || 'text-ink-3'}`}>
                        {e.level}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
